import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import { Badge } from '@bliks-io/ui';
import { WizardCallout } from './WizardCallout';

interface WizardStepHeaderProps {
  title: string;
  description?: string;
  hint?: string;
  /** Green Abgeschlossen badge next to the title + the done callout below */
  isDone?: boolean;
  /**
   * Callout copy while done. Defaults to the optional-step wording
   * ("re-entry stays additive"); the anchor steps pass their edit/delete
   * copy.
   */
  doneNoticeKeys?: { titleKey: string; descriptionKey: string };
  /** Rendered top-right: ClearDraftsButton, upload button, … */
  action?: ReactNode;
}

/**
 * The one header of every wizard data step: title (+ done badge),
 * description and optional skip-hint on the left, the step's action
 * top-right, the done callout underneath. Keeps the six table steps
 * stylistically identical.
 */
export function WizardStepHeader({
  title,
  description,
  hint,
  isDone = false,
  doneNoticeKeys = {
    titleKey: 'initialSetup.doneNotice.optional.title',
    descriptionKey: 'initialSetup.doneNotice.optional.description'
  },
  action
}: WizardStepHeaderProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="mb-2.5 font-display text-[30px] font-bold tracking-[-0.02em] text-foreground">
              {title}
            </h2>
            {isDone && (
              <Badge colorScheme="success" className="mb-2.5 gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('initialSetup.doneBadge')}
              </Badge>
            )}
          </div>
          {description && <p className="max-w-[88ch] text-sm text-fg-secondary">{description}</p>}
          {hint && <p className="mt-2 text-[13px] italic text-fg-subtle">{hint}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>

      {isDone && (
        <WizardCallout variant="success" title={t(doneNoticeKeys.titleKey)}>
          {/* The anchor-step copy emphasizes "Weiter" per the design */}
          <Trans
            i18nKey={doneNoticeKeys.descriptionKey}
            components={{ bold: <span className="font-semibold text-foreground" /> }}
          />
        </WizardCallout>
      )}
    </>
  );
}
